import { useEffect, useState, useRef } from "react";
import { reduxForm, InjectedFormProps } from "redux-form";
import { Button, Form, Modal, Message } from "rsuite";
import avatar2 from "../../assets/icons/avatar2.jpeg";
import VsIcon from "shared/icons/VsIcon";
import ArrowTopIcon from "shared/icons/ArrowTopIcon";
import Tabs, { TabPanelProps } from "components/Tabs";
import ScheduleBracketForm from "components/ScheduleBracket";
import AddResultForm from "components/AddReultForm";
import { BracketResult, BracketTeam, Seed } from "core/models/Bracket";
import { Tournament } from "core/models/Tournament";
import { getDate, getTime } from "core/utils/Date";
import history from "App/History";
import { useDispatch } from "react-redux";

interface AddQualifierProps {
  onClose(): void;
  show: boolean;
  initialValues?: any;
  onSubmit(values: any): void;
  selectedTab: string;
  setSelectedTab(tab: string): void;
  bracketTeams: BracketTeam[];
  tournament: Tournament;
  seed: Seed;
  isEdit?: boolean;
  bracketResult?: BracketResult[];
  handleSubmitCheckIn: (bracketId: string, teamId: number) => void;
}

const handleSubmitFail = () => {
  setTimeout(() => {
    const firstElement = document.getElementsByClassName(
      "rs-error-message-wrapper"
    )[0];
    if (firstElement) {
      firstElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, 50);
};

type FinalProps = InjectedFormProps<{}, AddQualifierProps> & AddQualifierProps;

function AddQualifier({
  onClose,
  show,
  handleSubmit,
  initialValues,
  onSubmit,
  initialize,
  submitFailed,
  invalid,
  selectedTab,
  isEdit = false,
  setSelectedTab,
  bracketTeams,
  tournament,
  seed,
  bracketResult,
  handleSubmitCheckIn,
}: FinalProps) {
  const [formError, setFormError] = useState(false);
  const defaultValues = useRef<any>({});
  const isEditSchedule = seed?.checkInEnabled;
  const isEditResult = seed?.isCompleted === true ? true : false;
  let disableSchedule = seed?.isCompleted === true ? true : false;
  const bestOfNumber = Number(seed?.matchFormat?.replace(/\D/g, ""));
  const isRoundRobin =
    tournament?.bracketType === "Round Robin" &&
    bracketResult &&
    bracketResult?.length &&
    seed?.isCompleted
      ? true
      : false;
  const dispatch = useDispatch();

  const count = bracketResult?.length || 0;

  const NEXT_MATCH =
    bracketResult?.length && !seed?.isCompleted ? bracketResult.length + 1 : 1;

  const MATCH_NO_OPTIONS =
    tournament?.bracketType === "Round Robin"
      ? bracketResult?.length && seed?.isCompleted
        ? Array.from({ length: count }, (_, i) => ({
            label: `Match No ${i + 1}`,
            value: i + 1,
          }))
        : Array.from({ length: NEXT_MATCH }, (_, i) => {
            const matchNo = i + 1;

            return {
              label: `Match No ${matchNo}`,
              value: matchNo,
              disabled: matchNo !== NEXT_MATCH,
            };
          })
      : [
          {
            label: `Match No ${Array.from(
              { length: bestOfNumber },
              (_, i) => i + 1
            ).join(", ")}`,
            value: 1,
          },
        ];

  const DISABLED_VALUES =
    bracketResult?.length && seed?.isCompleted
      ? []
      : MATCH_NO_OPTIONS.map((m) => m.value).filter(
          (v) => Number(v) !== NEXT_MATCH
        );

  function getDateAndTime(anyDate: any) {
    let date = getDate(anyDate || "", "DD MMM");
    let time = getTime(anyDate || "", "hh:mm A");
    let combinedDateTime = date + "," + time;
    return combinedDateTime;
  }

  useEffect(() => {
    if (initialValues) {
      defaultValues.current = initialValues;
      initialize(initialValues);
    }
  }, [initialValues, initialize]);

  useEffect(() => {
    if (submitFailed) {
      setFormError(invalid);
    }
  }, [submitFailed, invalid]);

  const handleFormSubmit = (values: any) => {
    onSubmit(values);
  };

  const TABS: TabPanelProps[] = [
    {
      tabTitle: isEditSchedule ? "Reschedule Bracket" : "Schedule Bracket",
      key: "schedule-bracket",
    },
    {
      tabTitle: isEditResult ? "Edit Result" : "Add Result",
      key: "add-result",
    },
  ];

  const getActionLabel = () => {
    if (selectedTab === "schedule-bracket") {
      return isEditSchedule ? "Reschedule" : "Schedule";
    }
    return isEditResult ? "Edit Result" : "Add Result";
  };

  useEffect(() => {
    if (!bracketTeams || !tournament?.tournamentConfig) return;

    if (isRoundRobin && bracketResult && seed?.isCompleted) {
      const createPlayerResults = (teams: BracketTeam[], matchResult?: any) =>
        teams.flatMap((team) =>
          team.user_teams.map((user) => {
            const stats: Record<string, any> = {};

            tournament.tournamentConfig.forEach((cfg) => {
              stats[cfg.key] =
                matchResult?.playerResults?.find(
                  (p: any) => p.userId === user.userId
                )?.stats?.[cfg.key] ?? "";
            });

            return {
              userId: user.userId,
              userName: user?.inGameName || user.userName,
              teamId: team.id,
              stats,
            };
          })
        );

      const createTeamResults = (teams: BracketTeam[], matchResult?: any) =>
        teams.map((team) => ({
          teamId: team.id,
          score:
            matchResult?.results?.find((r: any) => r.teamId === team.id)
              ?.score ?? "",
        }));

      const matchFormatValues: Record<string, any> = {};
      const totalMatches = bracketResult?.length || 0;

      for (let matchNo = 1; matchNo <= totalMatches; matchNo++) {
        const existingMatch = bracketResult?.find((m) => m.match === matchNo);

        matchFormatValues[matchNo] = {
          results: createTeamResults(bracketTeams, existingMatch),
          playerResults: createPlayerResults(bracketTeams, existingMatch),
        };
      }

      initialize({
        match:
          bracketResult?.length && !seed?.isCompleted
            ? Number(bracketResult.length + 1)
            : 1,
        matchFormat: matchFormatValues,
      });
    } else {
      const results = bracketTeams.map((team) => ({
        teamId: team.id,
        score: "",
      }));

      const playerResults = bracketTeams.flatMap((team) => {
        return team.user_teams.map((user) => {
          const stats: Record<string, any> = {};
          tournament.tournamentConfig.forEach((cfg) => {
            stats[cfg.key] = "";
          });

          return {
            userId: user.userId,
            userName: user?.inGameName ||user.userName,
            teamId: team.id,
            stats,
          };
        });
      });

      initialize({
        match:
          bracketResult?.length && !seed?.isCompleted
            ? Number(bracketResult.length + 1)
            : 1,
        results,
        playerResults,
      });
    }
  }, [bracketTeams, isRoundRobin, bracketResult]);

  function handleChatRedirection() {
    history.push(
      `/playoff/tournaments/${tournament?.id}/match/chat/${seed?.bracketId}`
    );
  }

  return (
    <Modal backdrop="static" onHide={onClose} show={show} size="lg">
      <Modal.Header>
        {/* <Modal.Title>View Qualifier</Modal.Title> */}
      </Modal.Header>

      {formError && (
        <Message
          type="error"
          description="Fill all required fields before submitting"
        />
      )}
      <div className="match-detail-wrapper">
        <div className="match-round-details">
          <div className="match-name">Match {seed?.matchSequence}</div>
          <div className="dot"></div>
          <div className="round-name">{seed?.roundName}</div>
        </div>
        <div className="best-of-number">Best of {bestOfNumber} </div>
        {!isEditResult ? (
          <div className="match-team-view-detail-wrapper">
            <div className="team-details">
              {bracketTeams?.map((team, index) => {
                const seedTeam = seed?.teams?.find(
                  (t: any) => t?.id === team.id
                );

                return (
                  <div className="team-detail" key={team.id}>
                    <img
                      src={seedTeam?.image || avatar2}
                      alt="team"
                      className="team-icon"
                    />

                    <div>
                      <div className="team-name">{seedTeam?.name}</div>

                      <div
                        className={`team-check-in ${
                          seedTeam?.checkedIn ? "checked-in" : "not-checked-in"
                        }`}
                      >
                        {seedTeam?.checkedIn ? "Checked In" : "Not Checked In"}
                      </div>
                    </div>

                    {seedTeam && !seedTeam.checkedIn && (
                      <Button
                        color="green"
                        onClick={() =>
                          handleSubmitCheckIn(seed.bracketId, seedTeam.id)
                        }
                      >
                        Check In
                      </Button>
                    )}

                    {index === 0 && (
                      <div className="vs-icon">
                        <VsIcon />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {seed?.matchStart && (
              <div className="match-schedule">
                Match Scheduled at : {getDateAndTime(seed?.matchStart)}
              </div>
            )}
          </div>
        ) : (
          <div className="match-view-detail-wrapper">
            <div className="team-details-section">
              {bracketTeams?.map((team, index) => {
                const seedTeam = seed?.teams?.find(
                  (t: any) => t?.id === team.id
                );

                return (
                  <>
                    <div className="team-detail" key={team.id}>
                      <div className="team-detail-left">
                        <div className="team-img">
                          <img
                            src={seedTeam?.image || avatar2}
                            alt="team"
                            className="team-img"
                          />
                        </div>

                        <div className="team-details-name">
                          <div className="team-name">{seedTeam?.name}</div>

                          <div
                            className={`team-check-in ${
                              seedTeam?.checkedIn
                                ? "checked-in"
                                : "not-checked-in"
                            }`}
                          >
                            {seedTeam?.checkedIn
                              ? "Checked In"
                              : "Not Checked In"}
                          </div>
                        </div>

                        {seedTeam && !seedTeam.checkedIn && (
                          <Button
                            color="green"
                            onClick={() =>
                              handleSubmitCheckIn(seed.bracketId, seedTeam.id)
                            }
                          >
                            Check In
                          </Button>
                        )}
                      </div>

                      <div className="team-detail-right">
                        <div className="score">{seedTeam?.score ?? "-"}</div>
                      </div>
                    </div>
                    {index === 0 && (
                      <div className="vs-icon">
                        <VsIcon />
                      </div>
                    )}
                  </>
                );
              })}
            </div>

            <div className="match-schedule">
              Match Scheduled at : {getDateAndTime(seed?.matchStart)}
            </div>
          </div>
        )}
      </div>

      <div className="open-match-wrapper">
        <div className="horizontal-line"></div>
        <div className="open-match" onClick={handleChatRedirection}>
          <div className="open-match-name">Open Match Chat</div>
          <div className="arrow-icon">
            <ArrowTopIcon fontSize={24} color="#6e6af0" />
          </div>
        </div>
        <div className="horizontal-line"></div>
      </div>

      <div className="tab-section">
        <Tabs
          className="tabs-wrapper"
          tabs={TABS}
          appearance="subtle"
          active={selectedTab}
          onSelect={setSelectedTab}
        />
      </div>

      <Form fluid onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="form-container">
          {selectedTab === "schedule-bracket" ? (
            <>
              <ScheduleBracketForm />
            </>
          ) : (
            <Modal.Body>
              <AddResultForm
                bracketTeams={bracketTeams}
                tournamentConfig={tournament?.tournamentConfig}
                bestOfNumber={bestOfNumber}
                isRoundRobin={isRoundRobin}
                matchOptions={MATCH_NO_OPTIONS}
                disabledValues={DISABLED_VALUES}
              />
            </Modal.Body>
          )}
        </div>

        <Modal.Footer>
          <Button
            // disabled={disableSchedule && selectedTab === "schedule-bracket"}
            appearance="primary"
            type="submit"
          >
            {getActionLabel()}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default reduxForm<{}, AddQualifierProps>({
  form: "add-qualifier",
  destroyOnUnmount: true,
  onSubmitFail: handleSubmitFail,
})(AddQualifier);
